import { Flex, Box, Spinner, Text } from '@cad-arc';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { LocationSortOption, LocationStore } from '../../location-store';
import { LocationTableHeaderV1 } from './location-table-header-v1';
import { LocationTableAddressRowV1 } from './location-table-address-row-v1';
import { LocationStoreView } from '../../types/locations';
import { useLocationList } from '../../hooks/use-location-list';
import { CommonSize, getOneSizeSmaller } from '../../../common/helpers/utils';

export interface LocationListProps {
    store: LocationStore;
    query: string;
    isLoadingResults: boolean;
    onClickRow?: (id: number) => void;
    moveFocusNext?: () => void;
    inputRef?: React.MutableRefObject<HTMLInputElement | null>;
    size?: CommonSize;
    listBoxId?: string;
    optionIdPrefix: string;
}

export const esriAddressWidth = '50%';
export const resolvedAddressWidth = '50%';
export const crossStreetWidth = '30%';
export const dAreaWidth = '20%';

export const LocationTableV1: React.FC<LocationListProps> = observer(
    ({
        store,
        isLoadingResults,
        query,
        onClickRow,
        moveFocusNext,
        inputRef,
        size,
        listBoxId,
        optionIdPrefix,
    }) => {
        const sortMode = store.sortMode;
        const { groupCounts, groupHeaders, list } = store.data;
        const smallerSize = getOneSizeSmaller(size);

        // Property access for mobx update. (required, Otherwise mobx doesn't update and state doesn't change)
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        groupCounts.length;
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        groupHeaders.length;
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        list.length;
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        store.highlightedLocationIndex;

        function updateSort(option: LocationSortOption): void {
            if (sortMode?.option === option) {
                if (sortMode?.direction === 'ASC') {
                    store.setSort({ option, direction: 'DSC' });
                } else {
                    store.setSort(undefined);
                }
            } else {
                store.setSort({ option, direction: 'ASC' });
            }
        }

        const locationList = toJS(list) || [];
        const dAreaLabel = locationList.reduce((label: string, location: LocationStoreView) => {
            if (location.dispatchAreaLabel.length > label.length) {
                return location.dispatchAreaLabel;
            }
            return label;
        }, '');

        const {
            mark43Items,
            esriItems,
            esriCustomItems,
            forcedResults,
            latLngItems,
        } = useLocationList({
            onClickRow,
            inputRef,
            moveFocusNext,
            store,
        }).data;

        return (
            <>
                <Box flexGrow={1} textAlign="left">
                    <Flex borderTop="sm" borderBottom="sm" borderColor="border.default" pr="var(--scrollbar-width)">
                        <LocationTableHeaderV1
                            currentSortMode={sortMode}
                            sortOption="ADDRESS"
                            updateSort={updateSort}
                            width={resolvedAddressWidth}
                            size={smallerSize}
                        >
                            resolved address
                        </LocationTableHeaderV1>
                        <LocationTableHeaderV1
                            currentSortMode={sortMode}
                            sortOption="D-AREA"
                            updateSort={updateSort}
                            width={dAreaWidth}
                            size={smallerSize}
                        >
                            <Text fontWeight="bold">d-area</Text>
                            <Text fontWeight="normal" casing="none" lineHeight="12px">
                                {dAreaLabel}
                            </Text>
                        </LocationTableHeaderV1>
                        <LocationTableHeaderV1
                            currentSortMode={sortMode}
                            sortOption="CROSS STREET"
                            updateSort={updateSort}
                            width={crossStreetWidth}
                            size={smallerSize}
                        >
                            cross streets
                        </LocationTableHeaderV1>
                    </Flex>
                </Box>
                <Box h="300px" fontFamily="jaf-facitweb, monospace">
                    {isLoadingResults ? (
                        <Flex w="100%" h="100%" alignItems="center" justifyContent="center">
                            <Spinner />
                        </Flex>
                    ) : (
                        <Box
                            w="100%"
                            h="100%"
                            overflowY="auto"
                            id={listBoxId}
                            role="listbox"
                            data-testid="location-select-results-listbox"
                        >
                            {esriCustomItems.length > 0 ? (
                                <>
                                    {esriCustomItems.map((location, index) => (
                                        <LocationTableAddressRowV1
                                            onClick={() => {
                                                onClickRow?.(location.id);
                                                store.setSelectedLocation(location);
                                                inputRef?.current?.focus?.();
                                                moveFocusNext?.();
                                            }}
                                            key={location.id}
                                            location={location}
                                            query={query}
                                            store={store}
                                            index={index}
                                            size={smallerSize}
                                            optionIdPrefix={optionIdPrefix}
                                        />
                                    ))}
                                </>
                            ) : undefined}
                            {mark43Items.length > 0 ? (
                                <>
                                    {mark43Items.map((location, index) => (
                                        <LocationTableAddressRowV1
                                            onClick={() => {
                                                onClickRow?.(location.id);
                                                store.setSelectedLocation(location);
                                                inputRef?.current?.focus?.();
                                                moveFocusNext?.();
                                            }}
                                            key={location.id}
                                            location={location}
                                            query={query}
                                            store={store}
                                            index={index + esriCustomItems.length}
                                            size={smallerSize}
                                            optionIdPrefix={optionIdPrefix}
                                        />
                                    ))}
                                </>
                            ) : undefined}
                            {esriItems.length > 0 ? (
                                <>
                                    <Box pl={3} pt={3} color="brand.default" bg="surface.primary">
                                        <Text fontSize={smallerSize}>Results from ESRI</Text>
                                    </Box>
                                    {esriItems.map((location, index) => (
                                        <LocationTableAddressRowV1
                                            onClick={() => {
                                                onClickRow?.(location.id);
                                                store.setSelectedLocation(location);
                                                inputRef?.current?.focus?.();
                                                moveFocusNext?.();
                                            }}
                                            key={location.id}
                                            location={location}
                                            query={query}
                                            store={store}
                                            index={
                                                index + esriCustomItems.length + mark43Items.length
                                            }
                                            size={smallerSize}
                                            optionIdPrefix={optionIdPrefix}
                                        />
                                    ))}
                                </>
                            ) : undefined}
                            {latLngItems.length > 0 ? (
                                <>
                                    {latLngItems.map((location, index) => (
                                        <LocationTableAddressRowV1
                                            onClick={() => {
                                                onClickRow?.(location.id);
                                                store.setSelectedLocation(location);
                                                inputRef?.current?.focus?.();
                                                moveFocusNext?.();
                                            }}
                                            key={location.id}
                                            location={location}
                                            query={query}
                                            store={store}
                                            index={
                                                index +
                                                esriCustomItems.length +
                                                mark43Items.length +
                                                esriItems.length
                                            }
                                            size={smallerSize}
                                            optionIdPrefix={optionIdPrefix}
                                        />
                                    ))}
                                </>
                            ) : undefined}

                            {forcedResults.map((forcedResult, index) => {
                                return (
                                    <LocationTableAddressRowV1
                                        onClick={() => {
                                            onClickRow?.(forcedResult.id);
                                            store.setSelectedLocation(forcedResult);
                                            inputRef?.current?.focus?.();
                                            moveFocusNext?.();
                                        }}
                                        key={forcedResult.id}
                                        location={forcedResult}
                                        store={store}
                                        index={
                                            index +
                                            esriCustomItems.length +
                                            mark43Items.length +
                                            esriItems.length +
                                            latLngItems.length
                                        }
                                        query={query}
                                        size={smallerSize}
                                        optionIdPrefix={optionIdPrefix}
                                    />
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </>
        );
    }
);
